import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ScrollArea from "./ScrollArea.jsx";
import { MENU, MENU_ITEM } from "./ui/styles.js";
import { phAttrs } from "../lib/analytics/attrs.js";
import { ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";

const SCROLL_OPTIONS = Object.freeze({
  overflow: { x: "scroll", y: "hidden" },
  scrollbars: { autoHide: "scroll", autoHideDelay: 400 },
});

const SCROLL_STEP = 160;

function Tab({ label, active, dot, title, onSelect, onClose, onMenu, menuOpen = false, onExport, exporting = false, index, dragging = false, dropPos = null, onPointerDown, kind = null }) {
  const tabRef = useRef(null);
  // Track whether the most-recent pointerdown turned into a drag.
  // The handleTitleClick handler reads this ref and ignores the
  // synthetic click that fires after a pointerup so a drop doesn't
  // double as a tab activation.
  const wasDragRef = useRef(false);

  // Clicking an already-active tab toggles the scope-step menu when
  // the parent provides one. Inactive tabs always switch via onSelect.
  // Passing the whole tab element as the anchor lets the parent align
  // the popover to the tab's own bounds rather than to the tiny
  // chevron icon, which previously overflowed into adjacent UI.
  const handleTitleClick = useCallback(() => {
    if (wasDragRef.current) {
      wasDragRef.current = false;
      return;
    }
    if (active && onMenu) {
      onMenu(menuOpen ? null : tabRef.current);
      return;
    }
    onSelect?.();
  }, [active, onMenu, menuOpen, onSelect]);

  return (
    <div
      ref={tabRef}
      data-tab-index={index}
      // Pointerdown bubbles up from inner buttons too. The TabStrip's
      // handler examines event.target and bails if the cursor is on
      // an interactive child (close, menu chevron) so those buttons
      // still behave normally — only drags initiated on the tab body
      // (title button background) start the reorder gesture.
      onPointerDown={onPointerDown ? (e) => onPointerDown(e, tabRef.current, wasDragRef) : undefined}
      // Tab background is layered: an opaque --bg base + the
      // translucent --control-bg tint as a gradient overlay. Without
      // the opaque base, the inactive tab inherits whatever surface
      // sits behind it — fine when that surface is also --bg, but
      // the Sheet's dot pattern bleeds through translucent
      // backgrounds. Layering makes the tabs read as solid panels
      // everywhere without changing how they look on plain --bg
      // hosts (tint over solid bg = same color either way).
      //
      // Inactive tabs keep their bottom border so they show the same
      // hairline the strip's bottom-border span draws across gaps —
      // an opaque tab body would otherwise mask it. Active tab still
      // omits the bottom border so it merges into the panel below.
      className={[
        "relative flex min-h-[38px] min-w-[100px] max-w-[220px] shrink-0 items-stretch overflow-hidden rounded-t-[7px] border bg-[color:var(--bg)]",
        active
          ? "border-b-0 border-[color:var(--line-strong)]"
          : "border-[color:var(--line)] [background-image:linear-gradient(var(--control-bg),var(--control-bg))]",
        dragging ? "opacity-40" : "",
      ].join(" ")}
    >
      <span
        className={[
          "pointer-events-none absolute inset-x-0 bottom-0 h-[1px]",
          active ? "bg-[color:var(--bg)]" : "bg-transparent",
        ].join(" ")}
        aria-hidden="true"
      />
      {/* Insertion indicator while dragging another tab over this
          one. Vertical accent bar at the left or right edge,
          depending on which half of this tab the cursor is over. */}
      {dropPos ? (
        <span
          aria-hidden="true"
          className={[
            "pointer-events-none absolute inset-y-0 w-[2px] bg-[color:var(--green)]",
            dropPos === "before" ? "left-0" : "right-0",
          ].join(" ")}
        />
      ) : null}
      <button
        type="button"
        onClick={handleTitleClick}
        title={title}
        {...phAttrs({ [ATTRIBUTE.TAB_KIND]: kind })}
        aria-haspopup={active && onMenu ? "menu" : undefined}
        aria-expanded={active && onMenu ? menuOpen : undefined}
        className="flex min-w-0 flex-1 items-center gap-[6px] px-[11px] py-[10px] text-left"
      >
        {dot ? <span className={`h-[8px] w-[8px] flex-none rounded-[999px] ${dot}`} /> : null}
        <span
          className={[
            "truncate text-[12px] leading-[1.06]",
            active ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]",
          ].join(" ")}
        >
          {label}
        </span>
      </button>
      {onMenu ? (
        // ▾ chevron next to ×. Same width/baseline as the close
        // button so the active tab visually carries a matched pair
        // of triggers. Click toggles the popover; the popover is
        // anchored to the whole tab so it aligns to the tab's edges.
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={`Scope steps for ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            onMenu(menuOpen ? null : tabRef.current);
          }}
          className={[
            "inline-flex w-[24px] shrink-0 items-center justify-center transition-colors [&_svg]:block [&_svg]:h-[12px] [&_svg]:w-[12px]",
            active
              ? "text-[color:var(--muted)] hover:text-[color:var(--ink)]"
              : "text-[color:var(--muted-soft)] hover:text-[color:var(--muted)]",
            menuOpen ? "text-[color:var(--ink)]" : "",
          ].join(" ")}
        >
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 5l3 3 3-3" />
          </svg>
        </button>
      ) : null}
      {onExport ? (
        // Per-tab "export to PDF" affordance — a small download icon,
        // same width/baseline as the scope chevron and close button so
        // the trailing controls stay aligned. Replaces the wide rail
        // "↓ pdf" button. Shows "…" while that tab is exporting.
        <button
          type="button"
          aria-label={`Export ${label} to PDF`}
          title="Export to PDF"
          disabled={exporting}
          onClick={(e) => { e.stopPropagation(); onExport(); }}
          className={[
            "inline-flex w-[24px] shrink-0 items-center justify-center transition-colors disabled:cursor-wait [&_svg]:block [&_svg]:h-[12px] [&_svg]:w-[12px]",
            active
              ? "text-[color:var(--muted)] hover:text-[color:var(--ink)]"
              : "text-[color:var(--muted-soft)] hover:text-[color:var(--muted)]",
          ].join(" ")}
        >
          {exporting ? (
            <span className="text-[11px] leading-none">…</span>
          ) : (
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 1.5v5.5M3.5 5L6 7.5 8.5 5M2.5 10.5h7" />
            </svg>
          )}
        </button>
      ) : null}
      {onClose ? (
        <button
          type="button"
          aria-label={`Close ${label}`}
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className={[
            "inline-flex w-[28px] shrink-0 items-center justify-center px-[8px] text-[12px] leading-none transition-colors",
            active
              ? "text-[color:var(--muted)] hover:text-[color:var(--ink)]"
              : "text-[color:var(--muted-soft)] hover:text-[color:var(--muted)]",
          ].join(" ")}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}

function ArrowBtn({ direction, onClick }) {
  return (
    <button
      type="button"
      aria-label={direction < 0 ? "Scroll tabs left" : "Scroll tabs right"}
      onClick={onClick}
      className={[
        "absolute top-1/2 z-[2] flex h-[24px] w-[24px] -translate-y-1/2 items-center justify-center rounded-[7px] border border-[color:var(--line)] bg-[color:var(--bg)]",
        direction < 0 ? "left-[6px]" : "right-[6px]",
      ].join(" ")}
    >
      <svg width="8" height="10" viewBox="0 0 8 10" fill="none" className="text-[color:var(--muted)]">
        {direction < 0 ? (
          <path d="M6 1L2 5L6 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <path d="M2 1L6 5L2 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        )}
      </svg>
    </button>
  );
}

function OverflowMenu({ tabs }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Position the menu below the button using fixed coordinates
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.right });
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);

  return (
    <div className="relative z-[2] shrink-0">
      <button
        ref={btnRef}
        type="button"
        // Layered bg matches the inactive Tab: opaque --bg base
        // with the --control-bg tint on top, so the button reads as
        // solid on dotted hosts (Sheet) without changing its look
        // on plain --bg hosts.
        className="flex min-h-[38px] w-[34px] items-center justify-center rounded-t-[7px] border border-b-0 border-[color:var(--line)] bg-[color:var(--bg)] [background-image:linear-gradient(var(--control-bg),var(--control-bg))] text-[color:var(--muted)] cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <svg viewBox="0 0 16 16" fill="currentColor" className="h-[12px] w-[12px]">
          <circle cx="3" cy="8" r="1.3" />
          <circle cx="8" cy="8" r="1.3" />
          <circle cx="13" cy="8" r="1.3" />
        </svg>
      </button>
      {open && createPortal(
        // Rendered into <body> so the dropdown escapes every parent
        // stacking context. The OverflowMenu's own `relative z-[2]`
        // wrapper would otherwise trap the menu inside a low-z
        // stacking context, letting a later same-z sibling (chart
        // tooltip etc.) paint over it.
        <div
          ref={menuRef}
          className={`fixed z-[99999] grid min-w-[200px] max-w-[300px] p-[4px] ${MENU}`}
          style={{ top: `${pos.top}px`, right: `${window.innerWidth - pos.left}px` }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={[
                `flex min-w-0 items-center gap-[6px] ${MENU_ITEM}`,
                tab.active ? "!bg-[color-mix(in_srgb,var(--ink)_10%,transparent)]" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => { tab.onSelect?.(); setOpen(false); }}
            >
              {tab.dot ? <span className={`h-[6px] w-[6px] flex-none rounded-[999px] ${tab.dot}`} /> : null}
              {/* min-w-0 + flex-1 unlocks truncate inside the flex
                  parent. Without min-w-0 the span's intrinsic width
                  is its content's, so the menu blew past its
                  max-w-[300px] for long trace summaries. */}
              <span className="min-w-0 flex-1 truncate text-[12px] leading-[1.08] text-[color:var(--ink)]" title={tab.label}>
                {tab.label}
              </span>
              {tab.onClose ? (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Close ${tab.label}`}
                  onClick={(e) => { e.stopPropagation(); tab.onClose(); setOpen(false); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); tab.onClose(); } }}
                  className="ml-auto shrink-0 text-[12px] leading-none text-[color:var(--muted-soft)] hover:text-[color:var(--muted)] cursor-pointer"
                >
                  ×
                </span>
              ) : null}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

export default function TabStrip({ tabs, className = "", actions = null, onReorder = null, leading = null }) {
  const hostRef = useRef(null);
  const stripRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Drag state lives in BOTH a ref (synchronous read inside event
  // handlers) and React state (renders the dragging/drop-target
  // visuals). pointermove writes both; the pointerup handler reads
  // the ref to pick up the most-recent target without waiting for a
  // re-render.
  const dragRef = useRef(null); // { fromIdx, toIdx, pos } | null
  const [drag, setDrag] = useState(null);
  const updateDrag = useCallback((next) => {
    dragRef.current = next;
    setDrag(next);
  }, []);

  const startDrag = useCallback((event, tabEl, wasDragRef) => {
    if (!onReorder) return;
    if (event.button !== 0) return;
    // Don't start a drag from the close / menu chevron buttons —
    // their clicks should pass through cleanly. Title button is
    // fine: clicks discriminate via wasDragRef.
    if (event.target.closest("button[aria-label^='Close'], button[aria-label^='Scope steps'], button[aria-label^='Export']")) return;

    const fromIdx = Number(tabEl.dataset.tabIndex);
    if (!Number.isFinite(fromIdx)) return;
    const startX = event.clientX;
    let active = false;

    const onMove = (ev) => {
      if (!active) {
        if (Math.abs(ev.clientX - startX) < 5) return;
        active = true;
        wasDragRef.current = true;
      }
      // Find the closest tab by horizontal midpoint. Y position is
      // ignored, so dragging vertically out of the strip still
      // resolves to a sensible target.
      const stripEl = stripRef.current;
      if (!stripEl) return;
      const tabEls = Array.from(stripEl.querySelectorAll("[data-tab-index]"));
      let bestIdx = null;
      let bestDist = Infinity;
      let pos = "after";
      for (const el of tabEls) {
        const r = el.getBoundingClientRect();
        const cx = (r.left + r.right) / 2;
        const d = Math.abs(ev.clientX - cx);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = Number(el.dataset.tabIndex);
          pos = ev.clientX < cx ? "before" : "after";
        }
      }
      if (bestIdx == null) return;
      updateDrag({ fromIdx, toIdx: bestIdx, pos });
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      const finalDrag = dragRef.current;
      updateDrag(null);
      if (active && finalDrag && finalDrag.fromIdx != null) {
        let to = finalDrag.toIdx + (finalDrag.pos === "after" ? 1 : 0);
        if (finalDrag.fromIdx < to) to -= 1;
        if (finalDrag.fromIdx !== to) {
          onReorder(finalDrag.fromIdx, to);
        }
      }
      // Reset wasDragRef on the next tick so the synthetic click
      // that fires after pointerup can read it. Inactive drags
      // never set it, so click flow stays normal.
      if (active) {
        setTimeout(() => { wasDragRef.current = false; }, 0);
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }, [onReorder, updateDrag]);

  const updateOverflow = useCallback(() => {
    const host = hostRef.current;
    if (!host) return;
    const viewport = host.querySelector("[data-overlayscrollbars-viewport]") || host;
    setCanScrollLeft(viewport.scrollLeft > 1);
    setCanScrollRight(viewport.scrollLeft < viewport.scrollWidth - viewport.clientWidth - 1);
    setOverflows(viewport.scrollWidth > viewport.clientWidth + 1);
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const viewport = host.querySelector("[data-overlayscrollbars-viewport]") || host;
    const onScroll = () => updateOverflow();
    viewport.addEventListener("scroll", onScroll, { passive: true });

    const ro = new ResizeObserver(updateOverflow);
    ro.observe(viewport);
    const content = host.querySelector("[data-scroll-content]") || viewport.firstElementChild;
    if (content) ro.observe(content);

    updateOverflow();
    return () => {
      viewport.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [tabs.length, updateOverflow]);

  const scrollBy = useCallback((dir) => {
    const host = hostRef.current;
    if (!host) return;
    const viewport = host.querySelector("[data-overlayscrollbars-viewport]") || host;
    viewport.scrollBy({ left: dir * SCROLL_STEP, behavior: "smooth" });
  }, []);

  // Read the freshest tabs inside the deferred scroll without making the
  // effect below depend on `tabs` (whose identity changes on every parent
  // render) — we only want to scroll when the *focused* tab changes.
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  // Auto-scroll the active tab into view whenever the focused tab actually
  // changes — keyed on the active tab's id (not its index), so a reorder
  // that leaves focus put won't yank the strip, and a freshly-opened tab
  // far to the right is pulled into view. Covers cold load too (the first
  // active id is a change from none). Manual, horizontal-only scroll:
  // el.scrollIntoView() would also move ancestor scroll containers
  // vertically, which we don't want.
  const activeTab = tabs.find((t) => t.active);
  const activeTabId = activeTab ? activeTab.id : null;
  useEffect(() => {
    if (activeTabId == null) return undefined;
    const host = hostRef.current;
    const stripEl = stripRef.current;
    if (!host || !stripEl) return undefined;
    const raf = requestAnimationFrame(() => {
      const viewport = host.querySelector("[data-overlayscrollbars-viewport]") || host;
      const idx = tabsRef.current.findIndex((t) => t.id === activeTabId);
      if (idx < 0) return;
      const el = stripEl.querySelector(`[data-tab-index="${idx}"]`);
      if (!el) return;
      const vp = viewport.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      // Pad past the overlay scroll arrows (w-[24px]) parked at each edge
      // so the active tab never lands hidden underneath one.
      const PAD = 28;
      if (er.left < vp.left + PAD) {
        viewport.scrollBy({ left: er.left - vp.left - PAD, behavior: "smooth" });
      } else if (er.right > vp.right - PAD) {
        viewport.scrollBy({ left: er.right - vp.right + PAD, behavior: "smooth" });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [activeTabId]);

  return (
    <div className={["relative min-w-0", className].join(" ")}>
      <span
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[1px] bg-[color:var(--line)]"
        aria-hidden="true"
      />
      <div className="flex min-w-0 items-stretch gap-[2px]">
        {/* Fixed leading slot — stays put while the tabs scroll. Used
            by the docked chat to pin the Context⇄Chat mode switch. */}
        {leading}
        <div className={[
          "relative z-[1] min-w-0 flex-1 overflow-hidden",
          canScrollLeft ? "border-l border-l-[color:var(--line)]" : "",
          canScrollRight ? "border-r border-r-[color:var(--line)]" : "",
        ].join(" ")}>
          {canScrollLeft && <ArrowBtn direction={-1} onClick={() => scrollBy(-1)} />}
          {canScrollRight && <ArrowBtn direction={1} onClick={() => scrollBy(1)} />}
          <ScrollArea
            options={SCROLL_OPTIONS}
            hostRef={hostRef}
            className="min-w-0"
            contentClassName="flex min-w-max items-stretch gap-[2px]"
          >
            <div ref={stripRef} className="flex min-w-max items-stretch gap-[2px]">
              {tabs.map((tab, i) => (
                <Tab
                  key={tab.id}
                  kind={tab.kind}
                  label={tab.label}
                  active={tab.active}
                  dot={tab.dot}
                  title={tab.title}
                  onSelect={tab.onSelect}
                  onClose={tab.onClose}
                  onMenu={tab.onMenu}
                  menuOpen={tab.menuOpen}
                  onExport={tab.onExport}
                  exporting={tab.exporting}
                  index={i}
                  dragging={drag?.fromIdx === i}
                  dropPos={drag && drag.toIdx === i && drag.fromIdx !== i ? drag.pos : null}
                  onPointerDown={onReorder ? startDrag : undefined}
                />
              ))}
            </div>
          </ScrollArea>
        </div>
        {overflows && <OverflowMenu tabs={tabs} />}
        {actions ? <div className="flex shrink-0 items-stretch pl-[14px]">{actions}</div> : null}
      </div>
    </div>
  );
}
